import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Chart as ChartJS } from 'chart.js';
import dayjs, { Dayjs } from 'dayjs';
import { Job } from '@core/generated/gql-types';
import { useThemeStore } from '@core/theme/useThemeStore';

interface JobsGraphProps {
  filledJobs: Job[];
  unfilledJobs: Job[];
  startDate: Dayjs | undefined;
  endDate: Dayjs | undefined;
}

const UNFILLED_JOBS_COLOUR = '#ADADAD';
const FALLBACK_COMPLETED_JOBS_COLOUR = '#ADADAD';

const JobsGraph = ({
  filledJobs,
  unfilledJobs,
  startDate,
  endDate,
}: JobsGraphProps) => {
  const { theme } = useThemeStore();
  const completedJobsColour =
    theme?.secondaryBrandColor ?? FALLBACK_COMPLETED_JOBS_COLOUR;
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<ChartJS | null>(null);
  const { labels, completedJobsData, unfilledJobsData } = useMemo(() => {
    const findJobsOnDate = (
      targetDate: Dayjs,
      jobsList: Job[],
      dateType: 'startDate' | 'endDate' = 'startDate',
    ): number => {
      return jobsList.filter((job) => {
        if (!job[dateType]) {
          return false;
        }

        const jobDate = dayjs(Number(job[dateType]));
        return jobDate.isSame(targetDate, 'day');
      }).length;
    };

    let effectiveStartDate = startDate;
    let effectiveEndDate = endDate;

    // If dates are not provided, calculate from all jobs.
    if (!startDate || !endDate) {
      const allJobs = [...filledJobs, ...unfilledJobs];
      const allJobsWithEndDates = allJobs.filter((job) => job.endDate);

      if (allJobsWithEndDates.length === 0) {
        return {
          labels: [],
          completedJobsData: [],
          unfilledJobsData: [],
        };
      }

      const endDateTimestamps = allJobsWithEndDates.map((job) =>
        Number(job.endDate),
      );
      const minTimestamp = Math.min(...endDateTimestamps);
      const maxTimestamp = Math.max(...endDateTimestamps);
      effectiveStartDate = dayjs(minTimestamp);
      effectiveEndDate = dayjs(maxTimestamp);
    }

    const generatedLabels: string[] = [];
    const completed: number[] = [];
    const unfilled: number[] = [];
    let currentDate = dayjs(effectiveStartDate);

    while (currentDate.isSameOrBefore(effectiveEndDate, 'day')) {
      generatedLabels.push(currentDate.format('DD/MM'));
      completed.push(findJobsOnDate(currentDate, filledJobs, 'endDate'));
      unfilled.push(findJobsOnDate(currentDate, unfilledJobs, 'startDate'));
      currentDate = currentDate.add(1, 'day');
    }

    return {
      labels: generatedLabels,
      completedJobsData: completed,
      unfilledJobsData: unfilled,
    };
  }, [startDate, endDate, filledJobs, unfilledJobs]);

  useEffect(() => {
    if (!chartRef.current) {
      return;
    }

    const context = chartRef.current.getContext('2d');
    if (!context) {
      return;
    }

    chartInstance.current = new ChartJS(context, {
      type: 'bar',
      data: {
        labels: [],
        datasets: [
          {
            label: 'Unfilled Jobs',
            data: [],
            backgroundColor: UNFILLED_JOBS_COLOUR,
            borderColor: UNFILLED_JOBS_COLOUR,
            borderWidth: 1,
            borderRadius: 2,
          },
          {
            label: 'Completed Jobs',
            data: [],
            backgroundColor: FALLBACK_COMPLETED_JOBS_COLOUR,
            borderColor: FALLBACK_COMPLETED_JOBS_COLOUR,
            borderWidth: 1,
            borderRadius: 2,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false,
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            grid: {},
            ticks: {
              stepSize: 1,
              precision: 0,
            },
          },
        },
      },
    });

    // Cleanup function to destroy chart when component unmounts.
    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }
    };
  }, []);

  const syncChartData = useCallback(() => {
    if (!chartInstance.current) {
      return;
    }

    const chart = chartInstance.current;
    const unfilledJobsDataset = chart.data.datasets[0];
    const completedJobsDataset = chart.data.datasets[1];
    if (!unfilledJobsDataset || !completedJobsDataset) {
      return;
    }

    chart.data.labels = labels;
    unfilledJobsDataset.data = unfilledJobsData;
    completedJobsDataset.data = completedJobsData;
    completedJobsDataset.backgroundColor = completedJobsColour;
    completedJobsDataset.borderColor = completedJobsColour;
    chart.update('none');
  }, [labels, unfilledJobsData, completedJobsData, completedJobsColour]);

  useEffect(() => {
    syncChartData();
  }, [syncChartData]);

  return (
    <div className="grid-row-2 grid grid-cols-1 flex-row gap-4">
      <div className="flex h-auto min-h-[140px] flex-col gap-4 rounded-xl bg-white p-4 shadow">
        <div className="text-md font-semibold">Number of Jobs</div>

        <div className="h-[300px] w-full">
          <canvas ref={chartRef}></canvas>
        </div>

        <div className="flex justify-center gap-1">
          {/* Unfilled Jobs */}
          <div className="flex flex-col items-start rounded-lg border border-gray-200 bg-white px-4 py-2">
            <div className="mb-1 flex items-center gap-2">
              <span
                className="mr-1 inline-block h-3 w-3 rounded"
                style={{ background: UNFILLED_JOBS_COLOUR }}
              />
              <span className="text-xs font-semibold text-gray-500">
                Unfilled Jobs
              </span>
            </div>
          </div>

          {/* Completed Jobs */}
          <div className="flex flex-col items-start rounded-lg border border-gray-200 bg-gray-100 px-4 py-2">
            <div className="mb-1 flex items-center gap-2">
              <span
                className="mr-1 inline-block h-3 w-3 rounded"
                style={{ background: completedJobsColour }}
              />
              <span className="text-xs font-semibold text-gray-800">
                Completed Jobs
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobsGraph;
